<script setup>
defineProps({
  table: {
    type: Object,
    required: true
  }
})
const hasSetRef = ref(false)
const nowHourRef = ref(null)
const setRef = (el, hour) => {
  if (!hour.past && !hasSetRef.value) {
    nowHourRef.value = el
    hasSetRef.value = true
  }
}
onMounted(() => {
  if (nowHourRef.value) {
    nowHourRef.value.scrollIntoView({ behavior: 'instant', inline: 'start', block: 'nearest' })
  }
})
</script>

<template>
  <div class="table">
    <ul class="head">
      <li class="date">
        <p>日</p>
      </li>
      <li class="time">
        <p>時</p>
      </li>
      <li class="weather">
        <p>天気</p>
      </li>
      <li class="rain">
        <p>降水</p>
      </li>
      <li class="temp">
        <p>気温</p>
      </li>
      <li class="wind">
        <p>風</p>
      </li>
      <li class="thunder">
        <p>雷</p>
      </li>
    </ul>
    <div
      class="body"
    >
      <div
        v-for="(content, index) in table"
        :key="index"
        class="group"
        :class="content.past && 'past'"
      >
        <div class="date">
          <p>{{ content.date }}</p>
        </div>
        <div class="content">
          <ul
            v-for="(hour, hourIndex) in content.contents"
            :ref="(el) => setRef(el, hour)"
            :key="hourIndex"
            class="list"
            :class="hour.past && 'past'"
          >
            <li class="time">
              <p>{{ hour.time }}</p>
            </li>
            <li class="weather">
              <figure>
                <img
                  :src="`https://gvs.weathernews.jp/onebox/img/wxicon/${hour.weather}.webp`"
                  loading="lazy"
                  alt=""
                >
              </figure>
            </li>
            <li class="rain">
              <p>{{ hour.rain === null ? '-' : hour.rain }}<span v-if="hour.rain !== '0' && hour.rain !== null">mm</span></p>
            </li>
            <li class="temp">
              <p>{{ hour.temp === null ? '-' : Math.round(hour.temp) }}<span>℃</span></p>
            </li>
            <li class="wind">
              <figure>
                <svg
                  v-if="hour.windSpd !== 0 && hour.windSpd !== null"
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="15"
                  viewBox="0 0 11 15"
                  :data-rank="hour.windRank"
                  :style="`transform: rotate(calc(1deg * ${hour.windDir*22.5}))`"
                >
                  <path
                    d="M9.91706 1.33286L5.48529 13.7418L1.05353 1.33286L5.31805 5.17093L5.48529 5.32145L5.65253 5.17093L9.91706 1.33286Z"
                    stroke="#333333"
                    stroke-width="0.5"
                  />
                </svg>
              </figure>
              <p>{{ hour.windSpd === null ? '-' : Math.round(hour.windSpd) }}<span>m</span></p>
            </li>
            <li class="thunder">
              <figure>
                <img
                  v-if="hour.thunderRank"
                  :src="`https://gvs.weathernews.jp/golf/image/thunder/icon_status_thunder_${hour.thunderRank}.svg`"
                  alt=""
                  loading="lazy"
                >
              </figure>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.table {
  display: flex;
  position: relative;
  width: 100%;
  height: 236px;
  border: solid 1px #dfe4f0;
  border-radius: 8px;
  background-color: #f9f9f9;
  overflow: hidden;
  .scroll {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgb(0 0 0 / .8);
    z-index: 100;
    transition: opacity .3s ease-in-out;
    img {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100px;
    }
    &.is-loaded {
      opacity: 0;
    }
  }
  &::after {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    width: 10px;
    height: 100%;
    background: linear-gradient(to right, rgba(0 0 0 / 0), rgba(0 0 0 / .3));
    content: "";
  }
  .head {
    display: flex;
    position: relative;
    flex-direction: column;
    width: 45px;
    height: 100%;
    background-color: #eef1f8;
    box-shadow: 3px 0 6px 0 rgba(0 0 0 / .3);
    z-index: 2;
    .date,
    .time {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 22px;
      border-bottom: solid 1px #dfe4f0;
    }
    .weather {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 44px;
      border-bottom: solid 1px #dfe4f0;
    }
    .rain,
    .temp {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 30px;
      border-bottom: solid 1px #dfe4f0;
    }
    .wind {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      border-bottom: solid 1px #dfe4f0;
    }
    .thunder {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 38px;
    }
    p {
      font-size: 14px;
      line-height: 14px;
      text-align: center;
    }
  }
  .body {
    position: relative;
    display: flex;
    flex: 1;
    overflow-x: scroll;
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 8px;
      background-color: #eef1f8;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 8px;
      background: #438fec;
    }
    .group {
      display: flex;
      flex-direction: column;
      &:not(:first-child) {
        border-left: solid 1px #dfe4f0;
      }
      &:nth-child(2n) {
        .date {
          background-color: #eef1f8;
        }
      }
      &:has(ul.past:last-child) {
        .date {
          p {
            color: #a3a3a3;
          }
        }
      }
      .date {
        display: flex;
        align-items: center;
        height: 22px;
        border-bottom: solid 1px #dfe4f0;
        p {
          position: sticky;
          padding-left: 5px;
          padding-right: 5px;
          left: 0;
          font-size: 13px;
          font-weight: 600;
          line-height: 13px;
          &:is(.past *) {
            color: #a3a3a3;
          }
        }
      }
      .content {
        display: flex;
        flex: 1;
        .list {
          display: flex;
          flex-direction: column;
          width: 52px;
          &:not(:last-child) {
            border-right: solid 1px #dfe4f0;
          }
        }
        .time {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 22px;
          border-bottom: solid 1px #dfe4f0;
          p {
            font-size: 15px;
            line-height: 15px;
            &:is(.past *) {
              color: #a3a3a3;
            }
          }
        }
        .weather {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 44px;
          border-bottom: solid 1px #dfe4f0;
          figure {
            display: flex;
            align-items: center;
            width: 50px;
            aspect-ratio: 152 / 112;
            &:is(.past *) {
              filter: grayscale(1);
              opacity: 0.45;
            }
            img {
              width: 100%;
            }
          }
        }
        .rain,
        .temp {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 30px;
          border-bottom: solid 1px #dfe4f0;
          p {
            font-size: 15px;
            line-height: 15px;
            font-weight: 600;
            span {
              margin-left: 2px;
              font-size: 10px;
              font-weight: 300;
            }
            &:is(.past *) {
              color: #a3a3a3;
            }
          }
        }
        .wind {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          border-bottom: solid 1px #dfe4f0;
          gap: 5px;
          height: 40px;
          figure {
            display: flex;
            align-items: center;
            width: 14px;
            height: 14px;
            svg {
              &[data-rank='0'] {
              fill: #ffffff;
              }
              &[data-rank='1'] {
                fill: #03aeef;
              }
              &[data-rank='2'] {
                fill: #05ad50;
              }
              &[data-rank='3'] {
                fill: #febf00;
              }
              &[data-rank='4'] {
                fill: #e86200;
              }
              &[data-rank='5'] {
                fill: #fe0000;
              }
            }
            &:is(.past *) {
              filter: grayscale(1);
              opacity: 0.45;
            }
          }
          p {
            font-size: 15px;
            line-height: 15px;
            font-weight: 600;
            span {
              margin-left: 2px;
              font-size: 10px;
              font-weight: 300;
            }
            &:is(.past *) {
              color: #a3a3a3;
            }
          }
        }
        .thunder {
          display: flex;
          align-items: center;
          justify-content: center;
          padding-block: 5px;
          min-height: 38px;
          border-bottom: solid 1px #dfe4f0;
          figure {
            display: flex;
            align-items: center;
            height: 20px;
            aspect-ratio: 3 / 2;
            &:is(.past *) {
              filter: grayscale(1);
              opacity: 0.45;
            }
          }
          img {
            height: 100%;
          }
        }
      }
    }
  }
}
</style>
